// pages/DashboardPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Truck, Shield, BarChart3, Clock, CheckCircle,
  TrendingUp, TrendingDown, Eye, Filter, Download, Calendar,
  MapPin, Map, Users, Activity, Zap, Globe, LineChart,
  PieChart, ArrowRight, RefreshCw, Bell, Star, Plus,
  Navigation, Target, Timer, Percent, CalendarDays, X,
  FileSpreadsheet
} from 'lucide-react';
import { Card, Button, Badge, LoadingSpinner } from '../components/ui';
import { API_BASE_URL, API_ENDPOINTS } from "../constants";
import * as XLSX from 'xlsx';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [jobsData, setJobsData] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    totalJobs: 0,
    inTransit: 0,
    efficiency: 0,
    fuelSavings: 0,
    completedJobs: 0,
    activeDrivers: 0,
    citiesCovered: 0,
    activeVehicles: 0
  });

  const getAllJobs = async () => {
    try {
      setLoading(true);
      const url = new URL(API_BASE_URL + API_ENDPOINTS.JOBSLIST);
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const res = await response.json();
      console.log('API Response:', res);

      if (res.data && Array.isArray(res.data)) {
        const formattedJobs = res.data.map((row) => ({
          id: row.job_id,
          jobNumber: `${row.job_code}/${row.job_id}`,
          status: row.status === 1 ? "Active" : "Inactive",
          statusCode: row.status,
          statusName: row.rt_status_master?.status_name || 'Unknown',
          totalDistance: parseInt(row.total_distance) || 0,
          totalDuration: parseInt(row.total_duration) || 0,
          createdAt: row.created_at,
          jobActualStartDate: row.job_actual_start_date,
          jobActualEndDate: row.job_actual_end_date,
          jobEndDate: row.job_end_date,
          jobCreateDate: row.job_create_date,
          createdBy: row.created_by,
          type: row.job_type || 'General',
          priority: row.priority || 'Medium',
          customerPO: row.customer_po || 'N/A',
          description: row.job_desc || 'No description'
        }));

        setJobsData(formattedJobs);
        generateRealDashboardData(formattedJobs);
      } else {
        console.error('Invalid data format:', res);
        setJobsData([]);
        // Set default values when no data
        setDashboardData({
          totalJobs: 0,
          inTransit: 0,
          efficiency: 0,
          fuelSavings: 0,
          completedJobs: 0,
          activeDrivers: 0,
          citiesCovered: 0,
          activeVehicles: 0
        });
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobsData([]);
      // Set default values on error
      setDashboardData({
        totalJobs: 0,
        inTransit: 0,
        efficiency: 0,
        fuelSavings: 0,
        completedJobs: 0,
        activeDrivers: 0,
        citiesCovered: 0,
        activeVehicles: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const filterJobsByDateRange = (jobs) => {
    if (!Array.isArray(jobs) || jobs.length === 0) {
      return [];
    }

    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    endDate.setHours(23, 59, 59, 999);

    return jobs.filter(job => {
      if (!job.createdAt) return false;
      const jobDate = new Date(job.createdAt);
      return jobDate >= startDate && jobDate <= endDate;
    });
  };

  const generateRealDashboardData = (jobs) => {
    if (!Array.isArray(jobs)) {
      console.error('Jobs is not an array:', jobs);
      return;
    }

    const filteredJobs = filterJobsByDateRange(jobs);
    console.log('Filtered jobs:', filteredJobs);

    const totalJobs = filteredJobs.length;
    const activeJobs = filteredJobs.filter(job => job.statusCode === 1).length;

    const inTransitJobs = filteredJobs.filter(job =>
      job.jobActualStartDate !== null && job.jobActualEndDate === null
    ).length;

    const completedJobs = filteredJobs.filter(job =>
      job.jobActualEndDate !== null ||
      job.statusName?.toLowerCase().includes('completed') ||
      job.statusName?.toLowerCase().includes('delivered')
    ).length;

    const totalDistance = filteredJobs.reduce((sum, job) => sum + (job.totalDistance || 0), 0);

    const totalDuration = filteredJobs.reduce((sum, job) => sum + (job.totalDuration || 0), 0);

    const uniqueDrivers = [...new Set(filteredJobs.map(job => job.createdBy).filter(Boolean))].length;

    const averageJobDistance = totalJobs > 0
      ? Math.round(totalDistance / totalJobs)
      : 0;

    const averageJobDuration = totalJobs > 0
      ? Math.round(totalDuration / totalJobs)
      : 0;

    const citiesCovered = Math.min(15, Math.max(1, Math.floor(totalDistance / 200)));
    const activeVehicles = Math.max(1, Math.floor(activeJobs * 0.7));

    const calculatedData = {
      totalJobs,
      inTransit: inTransitJobs,
      completedJobs,
      activeDrivers: uniqueDrivers,
      citiesCovered: totalDistance > 0 ? citiesCovered : 0,
      activeVehicles: activeJobs > 0 ? activeVehicles : 0,
      averageJobDistance,
      averageJobDuration,
      totalDistance,
      totalDuration
    };

    console.log('Dashboard data calculated:', calculatedData);
    setDashboardData(calculatedData);
  };

  // Excel Export Function
  const exportToExcel = async () => {
    try {
      setExporting(true);

      // Get filtered jobs data
      const filteredJobs = filterJobsByDateRange(jobsData);

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // 1. Dashboard Summary Sheet
      const summaryData = [
        ['Dashboard Summary'],
        ['Date Range:', formatDateRange()],
        ['Generated On:', new Date().toLocaleString()],
        [''],
        ['Metric', 'Value', 'Description'],
        ['Total Jobs', dashboardData.totalJobs, 'Total number of jobs in selected period'],
        ['Jobs In Transit', dashboardData.inTransit, 'Currently moving vehicles'],
        ['Completed Jobs', dashboardData.completedJobs, 'Successfully completed jobs'],
        ['Active Drivers', dashboardData.activeDrivers, 'Number of unique active drivers'],
        ['Cities Covered', dashboardData.citiesCovered, 'Number of cities covered'],
        ['Active Vehicles', dashboardData.activeVehicles, 'Number of active vehicles'],
        ['Average Job Distance', `${dashboardData.averageJobDistance} km`, 'Average distance per job'],
        ['Average Job Duration', `${dashboardData.averageJobDuration} hrs`, 'Average time to complete a job'],
        ['Total Distance', `${dashboardData.totalDistance} km`, 'Total distance covered by all jobs'],
        ['Total Duration', `${dashboardData.totalDuration} hrs`, 'Total time spent on all jobs']
      ];

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);

      // Style the summary sheet
      summarySheet['!cols'] = [
        { width: 25 },
        { width: 15 },
        { width: 40 }
      ];

      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Dashboard Summary');

      // 2. Jobs Details Sheet
      if (filteredJobs.length > 0) {
        const jobsForExport = filteredJobs.map(job => ({
          'Job ID': job.id,
          'Job Number': job.jobNumber,
          'Status': job.status,
          'Status Name': job.statusName,
          'Type': job.type,
          'Priority': job.priority,
          'Customer PO': job.customerPO,
          'Description': job.description,
          'Total Distance (km)': job.totalDistance,
          'Total Duration (hrs)': job.totalDuration,
          'Created At': job.createdAt ? new Date(job.createdAt).toLocaleDateString() : 'N/A',
          'Created By': job.createdBy || 'N/A',
          'Job Start Date': job.jobActualStartDate ? new Date(job.jobActualStartDate).toLocaleDateString() : 'Not Started',
          'Job End Date': job.jobActualEndDate ? new Date(job.jobActualEndDate).toLocaleDateString() : 'Not Completed',
          'Planned End Date': job.jobEndDate ? new Date(job.jobEndDate).toLocaleDateString() : 'N/A'
        }));

        const jobsSheet = XLSX.utils.json_to_sheet(jobsForExport);

        // Auto-width columns for jobs sheet
        const jobsColumns = Object.keys(jobsForExport[0]).map(key => ({
          width: Math.max(key.length, 15)
        }));
        jobsSheet['!cols'] = jobsColumns;

        XLSX.utils.book_append_sheet(workbook, jobsSheet, 'Jobs Details');
      }

      // 3. Statistics Sheet
      const statsData = [
        ['Job Statistics by Status'],
        [''],
        ['Status', 'Count', 'Percentage'],
        ['Active', filteredJobs.filter(job => job.statusCode === 1).length, `${((filteredJobs.filter(job => job.statusCode === 1).length / Math.max(filteredJobs.length, 1)) * 100).toFixed(1)}%`],
        ['Inactive', filteredJobs.filter(job => job.statusCode === 0).length, `${((filteredJobs.filter(job => job.statusCode === 0).length / Math.max(filteredJobs.length, 1)) * 100).toFixed(1)}%`],
        ['In Transit', dashboardData.inTransit, `${((dashboardData.inTransit / Math.max(filteredJobs.length, 1)) * 100).toFixed(1)}%`],
        ['Completed', dashboardData.completedJobs, `${((dashboardData.completedJobs / Math.max(filteredJobs.length, 1)) * 100).toFixed(1)}%`],
        [''],
        ['Job Distribution by Priority'],
        [''],
        ['Priority', 'Count', 'Percentage'],
        ...['High', 'Medium', 'Low'].map(priority => {
          const count = filteredJobs.filter(job => job.priority === priority).length;
          return [priority, count, `${((count / Math.max(filteredJobs.length, 1)) * 100).toFixed(1)}%`];
        })
      ];

      const statsSheet = XLSX.utils.aoa_to_sheet(statsData);
      statsSheet['!cols'] = [
        { width: 20 },
        { width: 10 },
        { width: 15 }
      ];

      XLSX.utils.book_append_sheet(workbook, statsSheet, 'Statistics');

      // Generate filename with date range
      const filename = `Dashboard_Report_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`;

      // Write and download the file
      XLSX.writeFile(workbook, filename);

      // Show success message (you can replace this with your notification system)
      console.log('Excel file exported successfully!');

    } catch (error) {
      console.error('Error exporting to Excel:', error);
      // Handle error (you can show a toast notification here)
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    getAllJobs();
  }, []);

  useEffect(() => {
    if (Array.isArray(jobsData) && jobsData.length > 0) {
      generateRealDashboardData(jobsData);
    }
  }, [dateRange, jobsData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await getAllJobs();
    setRefreshing(false);
  };

  const handleDateRangeChange = (field, value) => {
    const today = new Date().toISOString().split('T')[0];
    if (value > today) return;

    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDateRange = () => {
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);

    if (dateRange.startDate === dateRange.endDate) {
      return start.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }

    return `${start.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short'
    })} - ${end.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })}`;
  };

  const setQuickDateRange = (days) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days + 1);

    setDateRange({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    });
  };

  const calculatePercentageChange = (current, field) => {
    const baseValues = {
      totalJobs: 20,
      inTransit: 15,
      efficiency: 85,
      fuelSavings: 20000
    };

    const base = baseValues[field] || current;
    if (base === 0) return '+0%';

    const change = ((current - base) / base) * 100;
    const sign = change >= 0 ? '+' : '';
    return `${sign}${Math.round(change)}%`;
  };

  const stats = [
    {
      icon: Package,
      title: 'Total Jobs',
      value: dashboardData.totalJobs?.toString() || '0',
      change: calculatePercentageChange(dashboardData.totalJobs || 0, 'totalJobs'),
      trend: (dashboardData.totalJobs || 0) > 15 ? 'up' : 'down',
      color: 'blue',
      description: 'Total jobs in selected period',
      details: {
        active: dashboardData.totalJobs - dashboardData.completedJobs || 0,
        completed: dashboardData.completedJobs || 0
      },
      target: Math.max(50, (dashboardData.totalJobs || 0) * 2),
      bgGradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: Truck,
      title: 'In Transit',
      value: dashboardData.inTransit?.toString() || '0',
      change: calculatePercentageChange(dashboardData.inTransit || 0, 'inTransit'),
      trend: (dashboardData.inTransit || 0) > 10 ? 'up' : 'down',
      color: 'emerald',
      description: 'Currently moving vehicles',
      details: {
        'on time': Math.floor((dashboardData.inTransit || 0) * 0.8),
        'delayed': Math.floor((dashboardData.inTransit || 0) * 0.2)
      },
      target: Math.max(25, (dashboardData.inTransit || 0) * 1.5),
      bgGradient: 'from-emerald-500 to-emerald-600'
    },
    {
      icon: Map,
      title: 'Avg. Job Distance',
      value: `${dashboardData.averageJobDistance || 0} km`,
      change: '+0%',
      trend: 'up',
      color: 'indigo',
      description: 'Average distance per job',
      details: {
        'total jobs': `${dashboardData.totalJobs || 0}`,
        'total distance': `${Math.floor((dashboardData.totalJobs || 0) * (dashboardData.averageJobDistance || 0))} km`
      },
      target: '500 km',
      bgGradient: 'from-indigo-500 to-indigo-600'
    },
    {
      icon: Timer,
      title: 'Avg. Job Duration',
      value: `${dashboardData.averageJobDuration || 0} hrs`,
      change: '+0%',
      trend: 'down',
      color: 'orange',
      description: 'Average time to complete a job',
      details: {
        'total duration': `${dashboardData.totalDuration || 0} hrs`,
        'jobs counted': dashboardData.totalJobs || 0
      },
      target: '4 hrs',
      bgGradient: 'from-orange-500 to-orange-600'
    }
  ];

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  const StatCard = ({ icon: Icon, title, value, change, trend, color, description, details, target, bgGradient }) => {
    const numericValue = parseInt(value.replace(/[^0-9]/g, '')) || 0;
    const numericTarget = typeof target === 'string' ? parseInt(target.replace(/[^0-9]/g, '')) : target || 100;
    const progressPercentage = Math.min((numericValue / numericTarget) * 100, 100);

    return (
      <Card hover className="relative overflow-hidden group transition-all duration-300 hover:shadow-xl">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className={`p-3 rounded-xl bg-gradient-to-r ${bgGradient} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className={`flex items-center text-sm font-medium transition-colors duration-200 ${trend === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
                {trend === 'up' ? (
                  <TrendingUp className="w-4 h-4 mr-1" />
                ) : (
                  <TrendingDown className="w-4 h-4 mr-1" />
                )}
                {change}
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
            <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
            <p className="text-xs text-gray-500">{description}</p>
          </div>

          {/* Progress to target */}
          {target && (
            <div className="mb-4">
              <div className="flex justify-between text-xs text-gray-500 mb-2">
                <span>Progress to target</span>
                <span>{target}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-2 rounded-full bg-gradient-to-r ${bgGradient} transition-all duration-1000 ease-out`}
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Additional details */}
          {details && (
            <div className="pt-4 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-3 text-xs">
                {Object.entries(details).map(([key, val]) => (
                  <div key={key} className="flex justify-between items-center">
                    <span className="text-gray-500 capitalize">{key}:</span>
                    <span className="font-medium text-gray-700">{val}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  };

  const DateRangePicker = () => (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowDatePicker(!showDatePicker)}
        className="flex items-center space-x-2"
      >
        <CalendarDays className="w-4 h-4" />
        <span>{formatDateRange()}</span>
      </Button>

      {showDatePicker && (
        <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 w-80">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Select Date Range</h3>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setShowDatePicker(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant="outline"
                size="xs"
                onClick={() => setQuickDateRange(1)}
                className="text-xs"
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="xs"
                onClick={() => setQuickDateRange(7)}
                className="text-xs"
              >
                7 Days
              </Button>
              <Button
                variant="outline"
                size="xs"
                onClick={() => setQuickDateRange(30)}
                className="text-xs"
              >
                30 Days
              </Button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  min={dateRange.startDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end pt-3 border-t">
              <Button
                size="sm"
                onClick={() => setShowDatePicker(false)}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const QuickStatsCards = () => (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {[
        {
          icon: Users,
          value: dashboardData.activeDrivers?.toString() || '0',
          label: 'Active Drivers',
          color: 'blue',
          bgGradient: 'from-blue-500 to-blue-600'
        },
        {
          icon: Globe,
          value: dashboardData.citiesCovered?.toString() || '0',
          label: 'Cities Covered',
          color: 'emerald',
          bgGradient: 'from-emerald-500 to-emerald-600'
        },
        {
          icon: CheckCircle,
          value: `${dashboardData.completedJobs || 0}`,
          label: 'Completed Jobs',
          color: 'green',
          bgGradient: 'from-green-500 to-green-600'
        },
        {
          icon: Star,
          value: dashboardData.activeVehicles?.toString() || '0',
          label: 'Active Vehicles',
          color: 'purple',
          bgGradient: 'from-purple-500 to-purple-600'
        }
      ].map((stat, index) => {
        const IconComponent = stat.icon;
        return (
          <Card key={index} className="p-4 text-center group hover:shadow-lg transition-all duration-300">
            <div className={`w-12 h-12 bg-gradient-to-r ${stat.bgGradient} rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
              <IconComponent className="w-6 h-6 text-white" />
            </div>
            <p className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</p>
            <p className="text-sm text-gray-600">{stat.label}</p>
          </Card>
        );
      })}
    </div>
  );

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="p-4 sm:p-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
              Welcome back!
              <Zap className="w-6 h-6 text-yellow-500 ml-2 animate-pulse" />
            </h1>
            <p className="text-gray-600 mt-2 text-sm sm:text-base">
              Here's what's happening with your logistics operations.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <DateRangePicker />
            <Button
              variant="outline"
              size="sm"
              onClick={exportToExcel}
              loading={exporting}
              className="flex items-center space-x-2 hover:bg-green-50 hover:border-green-300"
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span>Export Excel</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleRefresh} loading={refreshing}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Main Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {stats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>

        {/* Quick Stats */}
        <QuickStatsCards />

        {/* No Data Message */}
        {/* {jobsData.length === 0 && !loading && (
          <Card className="p-8 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Available</h3>
            <p className="text-gray-600 mb-4">
              No jobs found for the selected date range. Try selecting a different date range or check if jobs exist in your system.
            </p>
            <div className="flex justify-center space-x-3">
              <Button onClick={handleRefresh} loading={refreshing}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Data
              </Button>
              <Button
                variant="outline"
                onClick={exportToExcel}
                loading={exporting}
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export Empty Report
              </Button>
            </div>
          </Card>
        )} */}

        {/* Export Information Card - Only show when there's data */}
        {/* {jobsData.length > 0 && (
          <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Excel Export Available
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    Export your dashboard data and job details to Excel format for further analysis.
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    <span className="bg-white px-2 py-1 rounded">
                      📊 Dashboard Summary
                    </span>
                    <span className="bg-white px-2 py-1 rounded">
                      📋 Job Details ({filterJobsByDateRange(jobsData).length} jobs)
                    </span>
                    <span className="bg-white px-2 py-1 rounded">
                      📈 Statistics
                    </span>
                  </div>
                </div>
              </div>
              <Button
                onClick={exportToExcel}
                loading={exporting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Report
              </Button>
            </div>
          </Card>
        )} */}
      </div>
    </div>
  );
};

export default DashboardPage;